import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQuantity } from '@fewerbags/core';
import { STORAGE_LOCATIONS } from '@fewerbags/schema';
import type { GroceryItemDto, GroceryListDetail } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { LOCATION_LABEL, type StorageLocation } from '../../lib/pantryDisplay';
import { expirationHint, haveQty } from '../../pages/groceries/display';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { SelectField } from '../SelectField';

const STORAGE_OPTIONS = STORAGE_LOCATIONS.map((l) => ({ value: l, label: LOCATION_LABEL[l] }));

/**
 * "I have this" (D35): resolve a row from what is already at home. Nothing is
 * bought, so no price or store location is asked for; optionally the needed
 * amount goes into the pantry with the usual storage/expiry defaults
 * (PLAN §4.4). The row is struck through optimistically.
 */
export function HaveItSheet({
  open,
  onClose,
  listId,
  item,
}: {
  open: boolean;
  onClose: () => void;
  listId: string;
  item: GroceryItemDto | null;
}) {
  const qc = useQueryClient();
  const [addToPantry, setAddToPantry] = useState(true);
  const [expiresOn, setExpiresOn] = useState('');
  const [storageLocation, setStorageLocation] = useState<StorageLocation>('pantry');

  const itemId = item?.id ?? '';
  const qty = item ? haveQty(item) : 0;
  const unit = item?.unit ?? 'count';
  const detailKey = groceryKeys.detail(listId);

  const defaults = useQuery({
    queryKey: groceryKeys.checkoffDefaults(listId, itemId, item?.productId ?? null, qty, unit),
    queryFn: () =>
      api.groceries.items.checkoffDefaults(listId, itemId, {
        ...(item?.productId ? { productId: item.productId } : {}),
        ...(qty > 0 ? { qty } : {}),
        unit,
      }),
    enabled: open && item !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (open) setAddToPantry(true);
  }, [open, item]);

  useEffect(() => {
    const d = defaults.data;
    if (!d) return;
    setExpiresOn(d.expiresOn ?? '');
    setStorageLocation(d.storageLocation);
  }, [defaults.data]);

  const have = useMutation({
    mutationFn: () =>
      api.groceries.items.have(listId, itemId, {
        addToPantry,
        // Until the defaults arrive the server applies the same rules itself.
        ...(addToPantry && defaults.data
          ? { storageLocation, expiresOn: expiresOn ? expiresOn : null }
          : {}),
        clientKey: crypto.randomUUID(),
      }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<GroceryListDetail>(detailKey);
      const now = new Date().toISOString();
      qc.setQueryData<GroceryListDetail>(detailKey, (old) =>
        old
          ? {
              ...old,
              list: { ...old.list, checkedCount: old.list.checkedCount + 1 },
              items: old.items.map((i) =>
                i.id === itemId ? { ...i, checkedAt: now, haveAt: now } : i,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
    },
    onSuccess: (result) => {
      qc.setQueryData<GroceryListDetail>(detailKey, (old) =>
        old
          ? { ...old, items: old.items.map((i) => (i.id === result.item.id ? result.item : i)) }
          : old,
      );
      onClose();
    },
  });

  if (!item) {
    return (
      <Dialog open={open} onClose={onClose} title="Have it">
        <p className="text-sm text-muted">No item selected.</p>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={`Have ${item.ingredientName} at home`}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          It comes off the list without a purchase. Nothing is added to prices or store info.
        </p>

        {qty > 0 ? (
          <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={addToPantry}
              onChange={(e) => setAddToPantry(e.target.checked)}
              className="size-5 accent-brand"
            />
            Add {formatQuantity(qty, unit)} to the pantry
          </label>
        ) : null}

        {addToPantry && qty > 0 ? (
          <>
            {defaults.isPending ? <p className="text-sm text-muted">Loading defaults…</p> : null}
            {defaults.isError ? (
              <Alert tone="error">{messageForError(defaults.error)}</Alert>
            ) : null}
            <SelectField
              id="have-location"
              label="Storage location"
              options={STORAGE_OPTIONS}
              value={storageLocation}
              onChange={(e) => setStorageLocation(e.target.value as StorageLocation)}
            />
            <div className="flex flex-col gap-1">
              <Field
                id="have-expires"
                label="Expiry date"
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
              {defaults.data ? (
                <p className="text-xs text-muted">
                  {expirationHint(defaults.data.expirationRule, defaults.data.expirationBasis)}
                </p>
              ) : null}
            </div>
          </>
        ) : null}

        {have.isError ? <Alert tone="error">{messageForError(have.error)}</Alert> : null}

        <div className="flex gap-2">
          <Button onClick={() => have.mutate()} disabled={have.isPending}>
            {have.isPending ? 'Saving…' : 'Confirm'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={have.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
